import { useState } from 'react';

import { Button } from '../../button';

import {
  ASKERS,
  askerLabels,
  bestPieces,
  closeness,
  EMBED_TEXTS,
  embeddings,
  embedTextLabels,
  FIRST_EIGHT,
  formatNumber,
  HYBRID_QUESTIONS,
  HYBRID_REFINEMENT,
  hybridExpected,
  hybridHits,
  hybridSummary,
  hybridTops,
  hybridTransition,
  initialHybrid,
  initialPermission,
  initialSimilarity,
  initialSpace,
  NOT_RECORDED,
  PERMISSION_QUESTION,
  PERMISSION_REFINEMENT,
  permissionSearch,
  permissionSummary,
  permissionTransition,
  recallText,
  recordedCosine,
  requestJson,
  returnedText,
  sampleTerms,
  SEARCHES,
  searchLabels,
  SIM_PIECES,
  SIM_QUESTIONS,
  SIMILARITY_REFINEMENT,
  similaritySummary,
  similarityTransition,
  simPieceLabels,
  SPACE_NOT_WORDS,
  SPACE_REFINEMENT,
  spaceSummary,
  spaceTransition,
  sqlShape,
  supportReturned,
  toAsker,
  toEmbedText,
  toHybridQuestion,
  toSearch,
  toSimPiece,
  toSimQuestion,
  UNIT_LENGTH,
  VECTOR_LENGTH,
  vectorText,
  type HybridAction,
  type PermissionAction,
  type SimilarityAction,
  type SpaceAction,
} from './embed-models';
import { formatTokens, pieceTitle, questionText, verdict } from './know-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './embed-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function EmbedSpaceLab() {
  const [state, setState] = useState(initialSpace);
  const act = (action: SpaceAction) => setState((current) => spaceTransition(current, action));
  const embedding = embeddings[state.text];
  const summary = spaceSummary(state);
  return (
    <Lab
      title="What does the embeddings endpoint return?"
      eyebrow="Embeddings · a sentence as numbers"
      className="rel-lab embed-lab"
    >
      <Predict>What comes back for a sentence? Choose a text, then press Embed.</Predict>
      <div className="cb-controls">
        <label>
          Text
          <ModelSelect
            aria-label="Text"
            value={state.text}
            onValueChange={(value) => act({ type: 'text', value: toEmbedText(value) })}
            options={EMBED_TEXTS.map((text) => ({ value: text, label: embedTextLabels[text] }))}
          />
        </label>
        <Button size="sm" disabled={state.embedded} onClick={() => act({ type: 'embed' })}>
          Embed
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The request">
        <pre aria-label="The request">{requestJson(embedding.input)}</pre>
      </Panel>
      <Panel label={`The response, ${FIRST_EIGHT}`}>
        <pre aria-label="The response">
          {state.embedded ? vectorText(embedding.first) : '(not embedded yet)'}
        </pre>
      </Panel>
      {state.embedded ? (
        <Facts
          values={[
            ['Numbers', formatTokens(VECTOR_LENGTH)],
            ['Tokens charged', String(embedding.tokens)],
            ['Length', UNIT_LENGTH],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.embedded ? (
        <p className="rel-caption">
          {SPACE_NOT_WORDS} {SPACE_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EmbedSimilarityLab() {
  const [state, setState] = useState(initialSimilarity);
  const act = (action: SimilarityAction) =>
    setState((current) => similarityTransition(current, action));
  const cosine = recordedCosine(state.question, state.piece);
  const summary = similaritySummary(state);
  return (
    <Lab
      title="How close are two sentences?"
      eyebrow="Embeddings · closeness is similarity"
      className="rel-lab embed-lab"
    >
      <Predict>
        Which pair scores highest? Choose a question and a piece, then press Compare.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toSimQuestion(value) })}
            options={SIM_QUESTIONS.map((id) => ({ value: id, label: `${id} ${questionText(id)}` }))}
          />
        </label>
        <label>
          Piece
          <ModelSelect
            aria-label="Piece"
            value={state.piece}
            onValueChange={(value) => act({ type: 'piece', value: toSimPiece(value) })}
            options={SIM_PIECES.map((id) => ({ value: id, label: simPieceLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.compared} onClick={() => act({ type: 'compare' })}>
          Compare
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The dot product, three terms of 1,024">
        <pre aria-label="The dot product">
          {state.compared ? sampleTerms(state.question, state.piece) : '(not compared yet)'}
        </pre>
      </Panel>
      {state.compared && cosine !== null ? (
        <div
          className="embed-bar"
          role="img"
          aria-label={`Cosine ${formatNumber(cosine)} of ${UNIT_LENGTH}`}
        >
          <span style={{ width: `${Math.round(cosine * 100)}%` }} />
        </div>
      ) : null}
      {state.compared ? (
        <Facts
          values={[
            ['Cosine', cosine === null ? NOT_RECORDED : formatNumber(cosine)],
            ['Reads as', cosine === null ? NOT_RECORDED : closeness(cosine)],
            ['Best piece for this question', bestPieces[state.question]],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{SIMILARITY_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EmbedHybridLab() {
  const [state, setState] = useState(initialHybrid);
  const act = (action: HybridAction) => setState((current) => hybridTransition(current, action));
  const top = hybridTops[state.question][state.search];
  const summary = hybridSummary(state);
  return (
    <Lab
      title="Which search finds the expected piece?"
      eyebrow="Embeddings · keyword, vector, hybrid"
      className="rel-lab embed-lab"
    >
      <Predict>
        Which search finds the expected piece? Choose a question and a search, then press Search.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toHybridQuestion(value) })}
            options={HYBRID_QUESTIONS.map((id) => ({ value: id, label: questionText(id) }))}
          />
        </label>
        <label>
          Search
          <ModelSelect
            aria-label="Search"
            value={state.search}
            onValueChange={(value) => act({ type: 'search', value: toSearch(value) })}
            options={SEARCHES.map((search) => ({ value: search, label: searchLabels[search] }))}
          />
        </label>
        <Button size="sm" disabled={state.searched} onClick={() => act({ type: 'run' })}>
          Search
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Top three"
        columns={['Rank', 'Piece', 'Document']}
        template="minmax(0, 0.4fr) minmax(0, 0.6fr) minmax(0, 2fr)"
        rows={state.searched ? top.map((id, index) => [index + 1, id, pieceTitle(id)]) : []}
      />
      {state.searched ? (
        <Facts
          values={[
            ['Expected', hybridExpected(state.question)],
            ['Top five', verdict(hybridHits[state.question][state.search])],
            ['Recall@5 over seventeen', recallText(state.search)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{HYBRID_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EmbedPermissionLab() {
  const [state, setState] = useState(initialPermission);
  const act = (action: PermissionAction) =>
    setState((current) => permissionTransition(current, action));
  const result = permissionSearch(state.asker, state.filtered);
  const summary = permissionSummary(state);
  return (
    <Lab
      title="What does Ravi's search return for the refund policy?"
      eyebrow="Embeddings · only pieces the asker may read"
      className="rel-lab embed-lab"
    >
      <Predict>
        What does Ravi's search return? Choose who asks, then press Search the refund policy.
      </Predict>
      <div className="cb-controls">
        <label>
          Who asks
          <ModelSelect
            aria-label="Who asks"
            value={state.asker}
            onValueChange={(value) => act({ type: 'asker', value: toAsker(value) })}
            options={ASKERS.map((asker) => ({ value: asker, label: askerLabels[asker] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.filtered}
          onClick={() => act({ type: 'toggle-filter' })}
        >
          Filter in the query
        </Button>
        <Button size="sm" disabled={state.searched} onClick={() => act({ type: 'search' })}>
          Search the refund policy
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{PERMISSION_QUESTION}</pre>
      </Panel>
      <Panel label="The SQL shape">
        <pre aria-label="The SQL shape">{sqlShape(state.filtered)}</pre>
      </Panel>
      <Panel label="Pieces returned">
        <pre aria-label="Pieces returned">
          {state.searched ? returnedText(result) : '(not searched yet)'}
        </pre>
      </Panel>
      {state.searched ? (
        <Facts
          values={[
            ['Pieces returned', String(result.returned.length + result.more)],
            ['Support pieces returned', String(supportReturned(result))],
            ['Hidden after retrieval', result.hidden ?? 'nothing to hide'],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{PERMISSION_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
